import { type FC, type ReactNode } from 'react';

import { Box, Divider, Grid, Stack, Text } from '@eneco/sparky';

import { type CostSummaryGridProps, type CostSummaryGridRow } from './PriceDetails.types';
import { formatFormattedValue, resolveTypeIcon, useEuroFormatters } from './PriceDetails.utils';

const GRID_COLUMNS = { initial: '1fr 8ch 8ch', md: '1fr 96px 96px' } as const;
const GRID_GAP = { initial: '2', md: '3' } as const;

interface CostSummaryGridCellsProps {
  label: ReactNode;
  monthlyValue: ReactNode;
  yearlyValue: ReactNode;
  icon?: ReactNode;
}

const CostSummaryGridCells: FC<CostSummaryGridCellsProps> = ({ label, monthlyValue, yearlyValue, icon }) => (
  <Grid gridTemplateColumns={GRID_COLUMNS} columnGap={GRID_GAP}>
    <Stack direction="row" alignY="center" gap="2">
      {icon && <Box>{icon}</Box>}
      {label}
    </Stack>
    {monthlyValue}
    {yearlyValue}
  </Grid>
);

const CostSummaryValueCell: FC<{ children: ReactNode; bold?: boolean }> = ({ children, bold }) => (
  <Text size={{ initial: 'BodyS', md: 'BodyM' }} weight={bold ? 'bold' : undefined} align="right">
    {children}
  </Text>
);

const CostSummaryDataRow: FC<{
  row: CostSummaryGridRow;
  formatValue: (value: CostSummaryGridRow['monthlyValue']) => string;
}> = ({ row, formatValue }) => (
  <Box paddingX="3" paddingY="2">
    <CostSummaryGridCells
      icon={resolveTypeIcon(row.type, row.icon)}
      label={<Text size={{ initial: 'BodyS', md: 'BodyM' }}>{row.label}</Text>}
      monthlyValue={<CostSummaryValueCell>{formatValue(row.monthlyValue)}</CostSummaryValueCell>}
      yearlyValue={<CostSummaryValueCell>{formatValue(row.yearlyValue)}</CostSummaryValueCell>}
    />
  </Box>
);

const CostSummaryTotalRow: FC<{
  row: CostSummaryGridRow;
  formatValue: (value: CostSummaryGridRow['monthlyValue']) => string;
}> = ({ row, formatValue }) => (
  <Box padding="3">
    <CostSummaryGridCells
      icon={resolveTypeIcon(row.type, row.icon)}
      label={
        <Text size={{ initial: 'BodyS', md: 'BodyM' }} weight="bold">
          {row.label}
        </Text>
      }
      monthlyValue={<CostSummaryValueCell bold>{formatValue(row.monthlyValue)}</CostSummaryValueCell>}
      yearlyValue={<CostSummaryValueCell bold>{formatValue(row.yearlyValue)}</CostSummaryValueCell>}
    />
  </Box>
);

export const CostSummaryGrid: FC<CostSummaryGridProps> = ({
  totalLabel,
  monthlyHeaderLabel,
  yearlyHeaderLabel,
  rows,
  totalRow,
  betweenTotals,
  netTotalRow,
}) => {
  const { currency } = useEuroFormatters();
  const formatValue = (value: CostSummaryGridRow['monthlyValue']) => formatFormattedValue(currency, value);

  return (
    <Box>
      <Box padding="3">
        <CostSummaryGridCells
          label={
            <Text size={{ initial: 'BodyS', md: 'BodyM' }} weight="bold">
              {totalLabel}
            </Text>
          }
          monthlyValue={<CostSummaryValueCell bold>{monthlyHeaderLabel}</CostSummaryValueCell>}
          yearlyValue={<CostSummaryValueCell bold>{yearlyHeaderLabel}</CostSummaryValueCell>}
        />
      </Box>

      <Divider weight="heavy" />

      {rows.map(row => (
        <CostSummaryDataRow key={row.label} row={row} formatValue={formatValue} />
      ))}

      <Divider isDecorative={true} emphasis="high" />

      <CostSummaryTotalRow row={totalRow} formatValue={formatValue} />
      {betweenTotals}
      {netTotalRow && <CostSummaryTotalRow row={netTotalRow} formatValue={formatValue} />}
    </Box>
  );
};
